import React from 'react';
import App from 'next/app';
import Head from 'next/head';
import getConfig from 'next/config';
import Router from 'next/router';
import { ApolloProvider } from 'react-apollo';
import { createGlobalStyle } from 'styled-components';
import Cookies from 'universal-cookie';
import AuthenticationProvider from '@g4/next-furniture/Authentication/AuthenticationProvider';
import TSAccountProtectionProvider from '@g4/next-furniture/TSAccountProtectionProvider/TSAccountProtectionProvider';
import tracking from '@g4/g4-analytics';

import ErrorBoundary from '../components/ErrorBoundary';
import TrackingProvider from './app/TrackingProvider';
import withApolloClient from '../graphql/withApolloClient';
import { G4OptimizelyProvider } from '../graphql/components/G4OptimizelyProvider.js';
import { ThemeProvider, theme } from '../components/Boneyard';
import MaxymiserProvider from '../components/MaxymiserProvider';
import ModalProvider from '../components/ModalProvider';
import TravelersFormProvider from '../hooks/useTravelersFormContext';
import UpliftStateProvider from '../components/UpliftStateProvider';
import BookingPathProvider from '../graphql/components/BookingPathProvider';
import CartProvider from '../graphql/components/CartProvider';
import SeatSizesMapProvider from '../graphql/components/SeatSizesMapProvider';
import { UserContextProvider } from '@g4/next-furniture/UserContextProvider';
import CallCenterRecordingProvider from '../graphql/components/CallCenterRecordingProvider';
import OneTrustCookie from '@g4/next-furniture/OneTrust/OneTrustCookie';
import PaypalStateProvider from '../components/PaypalStateProvider';

const nextConfig = getConfig();

const GlobalStyles = createGlobalStyle`
  html,
  body,
  #__next {
    height: 100%;
  }

  * {
    :focus:not(:focus-visible) {
      outline: none;
    }
  }

  body {
    overflow-x: hidden;
    font-family: 'Montserrat', sans-serif;
  }

  .bg-white {
    background-color: white;
  }
`;

class MyApp extends App {
  componentDidMount() {
    // Retrieve the selected stadium game from local storage
    const buildVersion = getConfig().publicRuntimeConfig.buildVersion;
    const appVersion = getConfig()?.publicRuntimeConfig?.appVersion;
    const cookies = new Cookies(document && document.cookie);
    const { promo } = Router.query;
    const cid = cookies.cookies['_ga'];

    // Common GA4 data layer variables for booking path
    tracking.setDataLayerVars({
      customer_id: cid,
      drupal_version: buildVersion,
      search_manifest: cookies.cookies['transaction-id'],
      loyalty_points_total: 0,
      loyalty_points_redeemed: 0,
      qualified_user: 1,
      software_version: appVersion,
      affiliation: 'WWW - Booking',
    });
    if (promo) {
      cookies.set('voucher_promo_code', promo, { path: '/', secure: true });
    }
  }

  render() {
    const { Component, pageProps, apolloClient } = this.props;

    return (
      <>
        <OneTrustCookie
          oneTrustToken={nextConfig.publicRuntimeConfig.oneTrustToken}
        />
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1"
          />
          <link
            type="text/css"
            rel="stylesheet"
            href="/booking/public/fonts/fonts.css"
          />
        </Head>
        <GlobalStyles />
        {/*
          TODO second errorBoundary that does not rely on apollo provider (or alternate strategy)
        */}
        <ApolloProvider client={apolloClient}>
          <G4OptimizelyProvider>
            <MaxymiserProvider>
              <ErrorBoundary>
                <TSAccountProtectionProvider>
                  <TrackingProvider>
                    <UserContextProvider>
                      <CallCenterRecordingProvider>
                        <AuthenticationProvider>
                          <ThemeProvider theme={theme}>
                            <UpliftStateProvider>
                              <PaypalStateProvider>
                                <ModalProvider>
                                  <CartProvider>
                                    <SeatSizesMapProvider>
                                      <BookingPathProvider>
                                        <TravelersFormProvider>
                                          <Component {...pageProps} />
                                        </TravelersFormProvider>
                                      </BookingPathProvider>
                                    </SeatSizesMapProvider>
                                  </CartProvider>
                                </ModalProvider>
                              </PaypalStateProvider>
                            </UpliftStateProvider>
                          </ThemeProvider>
                        </AuthenticationProvider>
                      </CallCenterRecordingProvider>
                    </UserContextProvider>
                  </TrackingProvider>
                </TSAccountProtectionProvider>
              </ErrorBoundary>
            </MaxymiserProvider>
          </G4OptimizelyProvider>
        </ApolloProvider>
      </>
    );
  }
}

export default withApolloClient({ App: MyApp });
